import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CornerDownLeft, Search } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useDepotData } from '../../context/DepotDataContext';
import { NAV_ITEMS } from '../../constants/navigation';
import { getStatusMeta } from '../../constants/containers';
import { Badge } from '../ui';

// Szybkie wyszukiwanie (Ctrl+K).
//
// Odpowiada na konkretny scenariusz z bramy: kierowca podaje numer kontenera,
// a operator musi go natychmiast odnaleźć. Wcześniej wymagało to przejścia do
// widoku kontenerów, wpisania numeru w pole filtra i odczekania na zapytanie do
// Firestore. Tutaj wyszukiwanie działa na danych już wczytanych do pamięci,
// więc wyniki pojawiają się natychmiast, z dowolnego miejsca w aplikacji.

const MAX_RESULTS = 8;

export function CommandPalette({ isOpen, onClose, onNavigate, onOpenContainer }) {
  // Przeszukiwane jest to, co aplikacja trzyma w pamięci: kontenery aktywne
  // oraz wydane w ostatnich tygodniach. Starsze archiwum nie jest wczytywane
  // przy starcie (rośnie w nieskończoność), więc szukanie w nim odbywa się
  // przez zakładkę Archiwum z zakresem dat. Stopka okna o tym przypomina.
  const { containers, getStockName, recentArchiveDays } = useDepotData();
  const [queryText, setQueryText] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setQueryText('');
      setActiveIndex(0);
      // Opóźnienie o klatkę — element trafia do DOM dopiero po tym renderze.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  const results = useMemo(() => {
    const term = queryText.trim().toLowerCase();

    // Bez wpisanej frazy pokazujemy same skróty nawigacyjne — lista wszystkich
    // kontenerów nie niosłaby tu żadnej informacji.
    const navResults = NAV_ITEMS.filter((item) => !term || item.label.toLowerCase().includes(term)).map(
      (item) => ({ type: 'nav', id: `nav-${item.id}`, label: item.label, icon: item.icon, target: item.id })
    );

    if (!term) return navResults;

    const containerResults = containers
      .filter((container) => {
        const haystack = [
          container.containerNumber,
          container.gateInReference,
          container.gateOutReference,
          getStockName(container.associatedStockId),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(term);
      })
      .slice(0, MAX_RESULTS)
      .map((container) => ({ type: 'container', id: container.id, container }));

    return [...containerResults, ...navResults];
  }, [queryText, containers, getStockName]);

  // Podświetlenie mogłoby wskazywać poza skróconą listę po zawężeniu frazy.
  useEffect(() => {
    setActiveIndex((current) => (current >= results.length ? 0 : current));
  }, [results.length]);

  if (!isOpen) return null;

  const runResult = (result) => {
    if (!result) return;
    if (result.type === 'nav') onNavigate(result.target);
    else onOpenContainer(result.container);
    onClose();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      onClose();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % Math.max(results.length, 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => (current - 1 + results.length) % Math.max(results.length, 1));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      runResult(results[activeIndex]);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center bg-slate-950/60 p-4 pt-[12vh] backdrop-blur-sm animate-fade-in"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Szybkie wyszukiwanie"
        className="w-full max-w-xl animate-scale-in overflow-hidden rounded-lg border border-line bg-surface shadow-pop"
        onKeyDown={handleKeyDown}
      >
        <div className="flex items-center gap-2.5 border-b border-line px-3.5">
          <Search size={16} className="shrink-0 text-subtle" />
          <input
            ref={inputRef}
            value={queryText}
            onChange={(event) => setQueryText(event.target.value)}
            placeholder="Numer kontenera, referencja lub nazwa stocku…"
            className="h-12 w-full border-0 bg-transparent text-sm text-content outline-none placeholder:text-subtle"
          />
          <kbd className="hidden shrink-0 rounded border border-line bg-elevated px-1.5 py-0.5 font-sans text-2xs text-subtle sm:block">
            Esc
          </kbd>
        </div>

        <div className="max-h-80 overflow-y-auto p-1.5">
          {results.length === 0 ? (
            <div className="px-3 py-6 text-center">
              <p className="text-xs text-muted">Brak wyników dla „{queryText}”.</p>
              <p className="mt-1 text-2xs text-subtle">
                Wyszukiwanie obejmuje kontenery aktywne i wydane w ostatnich{' '}
                {recentArchiveDays} dniach. Starszych szukaj w zakładce Archiwum.
              </p>
            </div>
          ) : (
            results.map((result, index) => {
              const isActive = index === activeIndex;

              if (result.type === 'nav') {
                const Icon = result.icon;
                return (
                  <button
                    key={result.id}
                    type="button"
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => runResult(result)}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors',
                      isActive ? 'bg-elevated text-content' : 'text-muted'
                    )}
                  >
                    <Icon size={15} className="shrink-0" />
                    <span className="flex-1">Przejdź do: {result.label}</span>
                    {isActive && <CornerDownLeft size={13} className="text-subtle" />}
                  </button>
                );
              }

              const { container } = result;
              const meta = getStatusMeta(container.containerStatus);

              return (
                <button
                  key={result.id}
                  type="button"
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => runResult(result)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors',
                    isActive ? 'bg-elevated' : ''
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-content">
                      {container.containerNumber}
                    </span>
                    <span className="block truncate text-xs text-muted">
                      {container.containerType} · {getStockName(container.associatedStockId)}
                      {container.gateInReference ? ` · ${container.gateInReference}` : ''}
                    </span>
                  </span>
                  <Badge tone={meta.tone}>{meta.label}</Badge>
                </button>
              );
            })
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-line bg-elevated px-3.5 py-2 text-2xs text-subtle">
          <span>↑↓ nawigacja</span>
          <span>↵ otwórz</span>
          <span>Esc zamknij</span>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default CommandPalette;
